import React, { useState, useCallback, useEffect } from "react"
import TextField from "@mui/material/TextField"
import InputAdornment from "@mui/material/InputAdornment"
import SearchIcon from "@mui/icons-material/Search"
import Box from "@mui/material/Box"
import { debounce } from "lodash"

interface PipelineSearchFilterProps {
  value: { id?: string; name?: string } | undefined
  placeholder: string
  onChange: (value: { id?: string; name?: string } | undefined) => void
}

export default function PipelineSearchFilter({
  value,
  onChange,
  placeholder,
}: PipelineSearchFilterProps) {
  const [searchText, setSearchText] = useState(value?.name || "")
  const debouncedOnChange = useCallback(
    debounce((text: string) => {
      if (!text.trim()) {
        onChange({ name: undefined })
        return
      }

      const isNumber = /^\d+$/.test(text.trim())

      if (isNumber) {
        onChange({ id: text.trim() })
      } else {
        onChange({ name: text.trim() })
      }
    }, 500),
    [onChange]
  )

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const text = event.target.value
    setSearchText(text)
    debouncedOnChange(text)
  }

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault()
    }
  }

  useEffect(() => {
    if (!value) {
      setSearchText("")

      return
    }

    setSearchText(value?.name || value?.id || "")
  }, [value])

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel()
    }
  }, [debouncedOnChange])

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        placeholder={placeholder}
        value={searchText}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  )
}
